import { createSlice, createAsyncThunk, Action } from '@reduxjs/toolkit';
import type { PayloadAction, Middleware } from '@reduxjs/toolkit';

// Types
export interface ChatMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'system';  // ✅ Changed from 'sender' to 'role', 'agent' to 'assistant'
  content: string;
  timestamp: Date;
  status: 'sending' | 'sent' | 'delivered' | 'failed';
  messageType: 'text' | 'error' | 'info' | 'typing';
  context?: {  // ✅ Changed from 'metadata' to 'context'
    confidence?: string;  // ✅ Changed to string (e.g., "0.9")
    intent?: string;  // ✅ Changed from 'queryClassification' to 'intent'
    actions_taken?: string[];  // ✅ New field
    follow_up_required?: boolean;  // ✅ New field
    urgency?: string;
    processingTimeMs?: number;  // Keep for backward compatibility
    ticketUrl?: string;  // For Freshdesk ticket links
  };
  raw_data?: any;  // ✅ New field for debugging info
}

// FIX: Type-safe error codes instead of generic string
export type ChatErrorCode =
  | 'INIT_FAILED'
  | 'SESSION_CREATE_FAILED'
  | 'SESSION_FAILED'
  | 'SESSION_EXPIRED'
  | 'SEND_FAILED'
  | 'NETWORK_ERROR'
  | 'API_ERROR'
  | 'MAX_RETRIES_EXCEEDED'
  | 'TIMEOUT'
  | 'INVALID_SESSION'
  | 'VALIDATION_ERROR';

export interface ChatError {
  code: ChatErrorCode;  // ✅ Type-safe error codes
  message: string;
  timestamp: Date;
  recoverable: boolean;
  details?: string;
}

export interface ChatSession {
  id: string;  // Maps to session_id from API
  userId: string;  // Maps to user_id from API
  userName: string;  // Maps to user_name from API
  createdAt: Date;  // ✅ Changed from 'startTime' to 'createdAt' (maps to created_at)
  lastActivity: Date;
  status: 'active' | 'inactive' | 'expired';
  capabilities?: string[];  // ✅ New field from API
}

export interface ChatBotState {
  // UI State
  isOpen: boolean;
  isMinimized: boolean;
  isInitialized: boolean;
  
  // Session Management
  sessionId: string | null;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  
  // Message State
  messages: ChatMessage[];
  isLoading: boolean;
  isTyping: boolean;
  unreadCount: number;
  
  // User Context
  userId: string;
  userName: string;
  userRole: string;
  environment: 'sandbox' | 'production';
  
  // Error Handling
  error: ChatError | null;
  retryCount: number;
  lastError: string | null;
  
  // Performance Tracking
  metrics: {
    sessionStartTime: number;
    messageCount: number;
    avgResponseTime: number;
  };
}

const initialState: ChatBotState = {
  // UI State
  isOpen: false,
  isMinimized: false,
  isInitialized: false,
  
  // Session Management
  sessionId: null,
  isConnected: false,
  connectionStatus: 'disconnected',
  
  // Message State
  messages: [],
  isLoading: false,
  isTyping: false,
  unreadCount: 0,
  
  // User Context
  userId: '',
  userName: '',
  userRole: '',
  environment: 'production',
  
  // Error Handling
  error: null,
  retryCount: 0,
  lastError: null,
  
  // Performance Tracking
  metrics: {
    sessionStartTime: 0,
    messageCount: 0,
    avgResponseTime: 0,
  },
};

// Async Thunks
export const initializeChatBot = createAsyncThunk(
  'chatBot/initialize',
  async (payload: { userId: string; userName: string; userRole: string; environment: 'sandbox' | 'production' }) => {
    const { chatBotAPI } = await import('../services/chatBotAPI');
    
    // Check for existing session in sessionStorage
    const savedSessionId = sessionStorage.getItem('chatBot_sessionId');
    
    if (savedSessionId) {
      console.log('ChatBot - Attempting to restore session:', savedSessionId);
      
      // Try to validate existing session
      const validation = await chatBotAPI.validateSession(savedSessionId);
      
      if (validation.success && validation.session) {
        console.log('ChatBot - Session restored successfully:', validation.session);
        
        // Get message history
        const messages = await chatBotAPI.getMessageHistory(savedSessionId);
        
        return {
          ...payload,
          sessionId: validation.session.id,
          messages,
          restored: true
        };
      } else {
        console.log('ChatBot - Session validation failed, will create new session');
        // Clear invalid session data
        sessionStorage.removeItem('chatBot_sessionId');
        sessionStorage.removeItem('chatBot_sessionData');
        sessionStorage.removeItem('chatBot_messages');
      }
    }
    
    // No valid session found, return user context for new session creation
    return { ...payload, restored: false };
  }
);

export const createChatSession = createAsyncThunk(
  'chatBot/createSession',
  async (_) => {
    const { chatBotAPI } = await import('../services/chatBotAPI');
    const response = await chatBotAPI.createSession();
    
    if (response.success && response.session) {
      return response.session;
    } else {
      throw new Error(response.error?.message || 'Failed to create session');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'chatBot/sendMessage',
  async (payload: { content: string; sessionId: string }) => {
    const { chatBotAPI } = await import('../services/chatBotAPI');
    const response = await chatBotAPI.sendMessage(payload.content, payload.sessionId);
    
    if (response.success && response.userMessage && response.agentResponse) {
      return { 
        userMessage: response.userMessage, 
        agentMessage: response.agentResponse 
      };
    } else {
      throw new Error(response.error?.message || 'Failed to send message');
    }
  }
);

export const closeChatSession = createAsyncThunk(
  'chatBot/closeSession',
  async (sessionId: string) => {
    const { chatBotAPI } = await import('../services/chatBotAPI');
    await chatBotAPI.closeSession(sessionId);

    // Clear sessionStorage on successful close
    sessionStorage.removeItem('chatBot_sessionId');
    sessionStorage.removeItem('chatBot_sessionData');
    sessionStorage.removeItem('chatBot_messages');

    return { sessionId, closedAt: new Date() };
  }
);

export const createFreshdeskTicket = createAsyncThunk(
  'chatBot/createFreshdeskTicket',
  async (sessionId: string) => {
    const { chatBotAPI } = await import('../services/chatBotAPI');
    const response = await chatBotAPI.createFreshdeskTicket(sessionId);

    if (response.success) {
      return {
        success: true,
        ticketId: response.ticket_id,
        ticketUrl: response.ticket_url,
        subject: response.subject,
        status: response.status,
        priority: response.priority,
        createdAt: response.created_at,
        message: response.message
      };
    } else {
      throw new Error(response.error || 'Failed to create Freshdesk ticket');
    }
  }
);

const chatBotSlice = createSlice({
  name: 'chatBot',
  initialState,
  reducers: {
    // UI Actions
    openChat: (state) => {
      state.isOpen = true;
      state.isMinimized = false;
      if (state.unreadCount > 0) {
        state.unreadCount = 0;
      }
    },
    
    closeChat: (state) => {
      state.isOpen = false;
      state.isMinimized = false;
    },
    
    minimizeChat: (state) => {
      state.isMinimized = true;
    },
    
    maximizeChat: (state) => {
      state.isMinimized = false;
    },
    
    toggleChat: (state) => {
      if (state.isOpen) {
        state.isOpen = false;
        state.isMinimized = false;
      } else {
        state.isOpen = true;
        state.isMinimized = false;
        state.unreadCount = 0;
      }
    },
    
    // Typing Indicators
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload;
    },
    
    // Connection Status
    setConnectionStatus: (state, action: PayloadAction<ChatBotState['connectionStatus']>) => {
      state.connectionStatus = action.payload;
      state.isConnected = action.payload === 'connected';
    },
    
    // Message Management
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
      state.metrics.messageCount++;

      // Increment unread count if chat is closed and message is from assistant
      if (!state.isOpen && action.payload.role === 'assistant') {  // ✅ Changed from 'sender' to 'role'
        state.unreadCount++;
      }
    },
    
    updateMessageStatus: (state, action: PayloadAction<{ messageId: string; status: ChatMessage['status'] }>) => {
      const message = state.messages.find(msg => msg.id === action.payload.messageId);
      if (message) {
        message.status = action.payload.status;
      }
    },
    
    clearMessages: (state) => {
      state.messages = [];
      state.metrics.messageCount = 0;
    },
    
    // Error Handling
    setError: (state, action: PayloadAction<ChatError>) => {
      state.error = action.payload;
      state.lastError = action.payload.message;
    },
    
    clearError: (state) => {
      state.error = null;
      state.lastError = null;
      state.retryCount = 0;
    },
    
    incrementRetry: (state) => {
      state.retryCount++;
    },
    
    // User Context
    setUserContext: (state, action: PayloadAction<{
      userId: string;
      userName: string;
      userRole: string;
      environment: 'sandbox' | 'production';
    }>) => {
      state.userId = action.payload.userId;
      state.userName = action.payload.userName;
      state.userRole = action.payload.userRole;
      state.environment = action.payload.environment;
    },
    
    // Session Management
    setSession: (state, action: PayloadAction<{ sessionId: string }>) => {
      state.sessionId = action.payload.sessionId;
      state.connectionStatus = 'connected';
      state.isConnected = true;
      state.metrics.sessionStartTime = Date.now();
    },
    
    clearSession: (state) => {
      state.sessionId = null;
      state.isConnected = false;
      state.connectionStatus = 'disconnected';
      state.messages = [];
      state.metrics = {
        sessionStartTime: 0,
        messageCount: 0,
        avgResponseTime: 0,
      };
    },
    
    // Performance Metrics
    updateResponseTime: (state, action: PayloadAction<number>) => {
      const currentAvg = state.metrics.avgResponseTime;
      const count = state.metrics.messageCount;
      state.metrics.avgResponseTime = count > 0 ? 
        ((currentAvg * (count - 1)) + action.payload) / count : 
        action.payload;
    },
    
    // Reset State
    resetChatBot: () => initialState,
  },
  
  extraReducers: (builder) => {
    builder
      // Initialize Chat Bot
      .addCase(initializeChatBot.pending, (state) => {
        state.isLoading = true;
        state.connectionStatus = 'connecting';
      })
      .addCase(initializeChatBot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        state.userId = action.payload.userId;
        state.userName = action.payload.userName;
        state.userRole = action.payload.userRole;
        state.environment = action.payload.environment;
        state.connectionStatus = 'connected';
        state.isConnected = true;
        
        // Handle session restoration
        if (action.payload.restored && 'sessionId' in action.payload) {
          state.sessionId = action.payload.sessionId;
          state.messages = 'messages' in action.payload ? action.payload.messages || [] : [];
          state.metrics.sessionStartTime = Date.now();
          state.metrics.messageCount = state.messages.length;
        }
      })
      .addCase(initializeChatBot.rejected, (state, action) => {
        state.isLoading = false;
        state.connectionStatus = 'error';
        state.error = {
          code: 'INIT_FAILED',
          message: action.error.message || 'Failed to initialize chat bot',
          timestamp: new Date(),
          recoverable: true
        };
      })
      
      // Create Session
      .addCase(createChatSession.pending, (state) => {
        state.isLoading = true;
        state.connectionStatus = 'connecting';
      })
      .addCase(createChatSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessionId = action.payload.id;
        state.connectionStatus = 'connected';
        state.isConnected = true;
        state.metrics.sessionStartTime = Date.now();

        // Add welcome message using userName from session response (not state)
        // The session API returns the correct userName from the backend
        const userName = action.payload.userName || state.userName || 'buddy';
        const welcomeMessage: ChatMessage = {
          id: `welcome_${Date.now()}`,
          sessionId: action.payload.id,
          role: 'assistant',  // ✅ Changed from 'sender: agent' to 'role: assistant'
          content: `Hello ${userName}! I'm your support agent. How can I help you today?`,
          timestamp: new Date(),
          status: 'delivered',
          messageType: 'text'
        };
        state.messages.push(welcomeMessage);
      })
      .addCase(createChatSession.rejected, (state, action) => {
        state.isLoading = false;
        state.connectionStatus = 'error';
        state.error = {
          code: 'SESSION_FAILED',
          message: action.error.message || 'Failed to create chat session',
          timestamp: new Date(),
          recoverable: true
        };
      })
      
      // Send Message
      .addCase(sendMessage.pending, (state, action) => {
        state.isLoading = true;
        
        // Add user message immediately with sending status
        const userMessage: ChatMessage = {
          id: `temp_${Date.now()}`,
          sessionId: action.meta.arg.sessionId,
          role: 'user',  // ✅ Changed from 'sender' to 'role'
          content: action.meta.arg.content,
          timestamp: new Date(),
          status: 'sending',
          messageType: 'text'
        };
        state.messages.push(userMessage);
        state.metrics.messageCount++;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // Update the temporary user message
        const tempMessage = state.messages.find(msg => msg.status === 'sending');
        if (tempMessage) {
          tempMessage.id = action.payload.userMessage.id;
          tempMessage.status = action.payload.userMessage.status;
        }
        
        // Add agent response
        state.messages.push(action.payload.agentMessage);
        state.metrics.messageCount++;
        
        // Update response time metrics (check context for backward compatibility)
        const processingTime = action.payload.agentMessage.context?.processingTimeMs;
        if (processingTime) {
          const currentAvg = state.metrics.avgResponseTime;
          const count = Math.floor(state.metrics.messageCount / 2); // Only assistant responses
          state.metrics.avgResponseTime = count > 1 ?
            ((currentAvg * (count - 1)) + processingTime) / count :
            processingTime;
        }

        // Increment unread count if chat is closed
        if (!state.isOpen) {
          state.unreadCount++;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        
        // Update the sending message to failed
        const sendingMessage = state.messages.find(msg => msg.status === 'sending');
        if (sendingMessage) {
          sendingMessage.status = 'failed';
        }
        
        state.error = {
          code: 'SEND_FAILED',
          message: action.error.message || 'Failed to send message',
          timestamp: new Date(),
          recoverable: true
        };
      })
      
      // Close Session
      .addCase(closeChatSession.fulfilled, (state) => {
        state.sessionId = null;
        state.isConnected = false;
        state.connectionStatus = 'disconnected';
        state.messages = [];
        state.metrics = {
          sessionStartTime: 0,
          messageCount: 0,
          avgResponseTime: 0,
        };
      })

      // Create Freshdesk Ticket
      .addCase(createFreshdeskTicket.pending, (state) => {
        // Add loading system message
        const loadingMessage: ChatMessage = {
          id: `system_ticket_loading_${Date.now()}`,
          sessionId: state.sessionId || '',
          role: 'system',
          content: 'Creating support ticket...',
          timestamp: new Date(),
          status: 'delivered',
          messageType: 'info'
        };
        state.messages.push(loadingMessage);
      })
      .addCase(createFreshdeskTicket.fulfilled, (state, action) => {
        // Remove loading message
        state.messages = state.messages.filter(msg => !msg.content.includes('Creating support ticket...'));

        // Add success message as a regular assistant message
        const successMessage: ChatMessage = {
          id: `system_ticket_success_${Date.now()}`,
          sessionId: state.sessionId || '',
          role: 'assistant',
          content: `Support Ticket Created Successfully

Ticket ID: #${action.payload.ticketId}

Subject: ${action.payload.subject}

Status: ${action.payload.status}

Priority: ${action.payload.priority}

If you feel the ticket doesn't contain required details, please email us at support@moneyone.in with the ticket link.`,
          timestamp: new Date(),
          status: 'delivered',
          messageType: 'text',
          context: {
            ticketUrl: action.payload.ticketUrl  // Store URL in context for rendering
          }
        };
        state.messages.push(successMessage);
      })
      .addCase(createFreshdeskTicket.rejected, (state, action) => {
        // Remove loading message
        state.messages = state.messages.filter(msg => !msg.content.includes('Creating support ticket...'));

        // Add error message as a regular assistant message
        const errorMessage: ChatMessage = {
          id: `system_ticket_error_${Date.now()}`,
          sessionId: state.sessionId || '',
          role: 'assistant',
          content: `Ticket Creation Issue

We encountered an issue while creating your support ticket.

Error: ${action.error.message || 'Unable to create ticket at this time'}

Please email us at support@moneyone.in with your issue details and we'll be happy to help!`,
          timestamp: new Date(),
          status: 'delivered',
          messageType: 'text'
        };
        state.messages.push(errorMessage);
      });
  },
});

export const {
  openChat,
  closeChat,
  minimizeChat,
  maximizeChat,
  toggleChat,
  setTyping,
  setConnectionStatus,
  addMessage,
  updateMessageStatus,
  clearMessages,
  setError,
  clearError,
  incrementRetry,
  setUserContext,
  setSession,
  clearSession,
  updateResponseTime,
  resetChatBot,
} = chatBotSlice.actions;

// FIX: Type-safe persistence middleware
// Removed 'any' type for security and type safety

// Persistence Middleware for auto-saving critical state
export const chatBotPersistenceMiddleware: Middleware<{}, { chatBot: ChatBotState }> =
  (store) => (next) => (action: unknown) => {
    const result = next(action);
    const state = store.getState().chatBot;

    // Type guard to safely access action.type
    if (!action || typeof action !== 'object' || !('type' in action)) {
      return result;
    }

    const typedAction = action as Action;

    // Auto-save on these critical actions
    if ([
      'chatBot/setSession',
      'chatBot/addMessage',
      'chatBot/createSession/fulfilled',
      'chatBot/sendMessage/fulfilled'
    ].includes(typedAction.type)) {
    
    try {
      // Save session data
      if (state.sessionId) {
        sessionStorage.setItem('chatBot_sessionId', state.sessionId);
        sessionStorage.setItem('chatBot_messages', JSON.stringify(state.messages));
        sessionStorage.setItem('chatBot_userContext', JSON.stringify({
          userId: state.userId,
          userName: state.userName,
          userRole: state.userRole,
          environment: state.environment
        }));
      }
    } catch (error) {
      console.warn('ChatBot persistence middleware failed:', error);
    }
  }
  
  // Clear persistence on session close
  if (typedAction.type === 'chatBot/clearSession' || typedAction.type === 'chatBot/closeChatSession/fulfilled') {
    try {
      sessionStorage.removeItem('chatBot_sessionId');
      sessionStorage.removeItem('chatBot_messages');
      sessionStorage.removeItem('chatBot_userContext');
      sessionStorage.removeItem('chatBot_sessionData');
    } catch (error) {
      console.warn('ChatBot clear persistence failed:', error);
    }
  }
  
  return result;
};

export default chatBotSlice.reducer;